import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAccessToken } from "../features/userSlice";

import ErrorHandler from "./ErrorHandler";
import Loader from "./Loader";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import login_image from "../images/login_image.png";

import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";

import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import landleaselogo from "../images/landleaselogo.png";

import { signInWithPopup } from "firebase/auth";
import { auth } from "../firebaseConfig";

import SupportIcon from "@mui/icons-material/Support";
import IconButton from "@mui/material/IconButton";
import { useEffect } from "react";
import { LoginUser } from "../features/userSlice";
import provider from "../firebaseConfig";
import Support from "./Support";

const theme = createTheme();

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errorLogged, setErrorLogged] = useState(false);
  const [firebaseError, setFirebaseError] = useState(false);
  const [helpIconClicked, setHelpIconClicked] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { userLoading, userInfo, userError, userSuccess } = useSelector(
    (state) => state.user
  );
  const domain = "lendlease.com";
  const isValidEmail = (email) => {
    const regexPattern = new RegExp(`\\b\\w+@${domain}\\b`, "gi");
    return regexPattern.test(email);
  };
  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setHelpIconClicked(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isValidEmail(formData.email)) {
      setErrorLogged("Please enter the valid email");
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('assessmentId')
      navigate("/login");
    } else if (isValidEmail(formData.email)) {
      setErrorLogged(null);
      setFirebaseError(null);

      provider.addScope("profile");
      provider.addScope("openid");
      provider.addScope("email");
      provider.addScope("User.Read");

      signInWithPopup(auth, provider)
        .then((userCredential) => {
          const data = {
            email: userCredential.user.email,
            access_token: userCredential.user.accessToken,
          };
          dispatch(LoginUser(data));
          dispatch(setAccessToken(userCredential.user.accessToken));
        })
        .catch((error) => {
          console.log(error);
          setFirebaseError(error);
        });
    }
  };
  useEffect(() => {
    if (userSuccess) navigate("/home");
  }, [userSuccess, userLoading]);
  return (
    <>
      {userLoading && <Loader />}
      <ThemeProvider theme={theme}>
        <Grid container component="main" sx={{ height: "100vh" }}>
          <CssBaseline />
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            component={Paper}
            elevation={6}
            style={{ borderRadius: "0px" }}
            square
          >
            <Card style={{ height: "100%" }}>
              <CardMedia
                image={login_image}
                title="Login Page Image"
                style={{
                  position: "relative",
                  height: "100%",
                  backgroundSize: "100% 100%",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  width: "50%",
                  color: "white",
                  padding: "80px",
                  transform: "translateY(-50%)",
                }}
              >
                <Typography
                  style={{
                    fontFamily: "RalewayMedium",
                    letterSpacing: "-0.5px",
                  }}
                  variant="h3"
                >
                  Welcome to your Lendlease Embodied Carbon Advisor (LECA)
                </Typography>
                <Typography
                  style={{
                    marginTop: "30px",
                    fontSize: "1rem",
                    fontFamily: "RalewayMedium",
                    lineHeight: "24px",
                    letterSpacing: "0.15px",
                  }}
                  variant="body2"
                  sx={{ marginTop: "20px" }}
                >
                  A platform to estimate upfront carbon emissions for projects.
                  LECA facilitates early-stage decision-making, 
                  helping to deliver on client briefs and aligning projects with the Mission Zero target.
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            component={Paper}
            elevation={6}
            square
            sx={{
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              className="custom-form"
              sx={{ mt: 0.5, display: "flex", flexDirection: "column" }}
            >
              <Box>
                <Box
                  component="img"
                  sx={{
                    height: 34,
                    width: 48,
                    float: "left",
                    marginRight: "24px",
                  }}
                  alt="Logo"
                  src={landleaselogo}
                />

                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: "#23B4C3",
                    lineHeight: "1.125rem",
                    align: "left",
                    marginLeft: "1rem",
                  }}
                >
                  Lendlease
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: "#232323",
                    align: "left",
                    ml: "1rem",
                    mb: ".5rem",
                  }}
                >
                  Embodied Carbon Advisor
                </Typography>
              </Box>

              {errorLogged && <ErrorHandler error={errorLogged} />}
              {userError !== null && <ErrorHandler error={userError} />}
              {firebaseError && <ErrorHandler error={firebaseError} />}
              <Typography
                sx={{ fontSize: "1rem", color: "#232323", align: "left" }}
              >
                Please Log in Using Your SSO Credentials
              </Typography>
              <TextField
                margin="normal"
                required
                sx={{ width: 350 }}
                id="email"
                name="email"
                label="Email"
                style={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#64748B",
                  borderRadius: "4px",
                  textTransform: "none",
                }}
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="success"
                style={{
                  backgroundColor: "#A6E0E5",
                  color: "#555555",
                  fontSize: "16px",
                  boxShadow: "none",
                  textTransform: "none",
                }}
                sx={{ mt: 2, mb: 2, width: 350 }}
              >
                Log in with SSO
              </Button>
              <IconButton
                size="small"
                onClick={() => setHelpIconClicked(true)}
                sx={{ color: "#232323", mr: 4 }}
              >
                <SupportIcon sx={{ mt: -0.5 }} />
                &nbsp;Helpdesk
              </IconButton>
              {helpIconClicked && <Support />}
            </Box>
          </Grid>
        </Grid>
      </ThemeProvider>
    </>
  );
};

export default Login;
